<template>
  <div class="border-rounded mt-3">
    <p class="mb-3 text-muted">{{ $t('efficiencyTestDetails.comparisonCriteria.description') }}</p>

    <div class="criteria-table">
      <div class="criteria-header d-flex align-items-center">
        <div class="criteria-col-label">{{ $t('efficiencyTestDetails.comparisonCriteria.columns.criterion') }}</div>
        <div class="flex-grow-1">{{ $t('efficiencyTestDetails.comparisonCriteria.columns.description') }}</div>
        <div class="criteria-col-toggle text-right">
          {{ $t('efficiencyTestDetails.comparisonCriteria.columns.active') }}
        </div>
      </div>

      <div v-for="criterion in criteria" :key="criterion.key" class="criteria-row d-flex align-items-start">
        <div class="criteria-col-label font-weight-bold small">
          {{ $t('efficiencyTestDetails.comparisonCriteria.criteria.' + criterion.key + '.label') }}
        </div>
        <div class="flex-grow-1 pr-4">
          <div class="small text-muted">
            {{ $t('efficiencyTestDetails.comparisonCriteria.criteria.' + criterion.key + '.description') }}
          </div>

          <!-- months tolerance (MaturityDate, IssueDate, or any future criterion shaped the same way) -->
          <div
            v-if="criterion.value.enabled && hasField(criterion, 'toleranceMonths')"
            class="d-flex align-items-center mt-2"
          >
            <span class="small text-muted mr-2">{{ $t(fieldsPrefix + 'toleranceMonths') }}</span>
            <b-form-spinbutton
              v-model="criterion.value.toleranceMonths"
              min="0"
              max="60"
              size="sm"
              inline
            ></b-form-spinbutton>
            <span class="small text-muted ml-2">{{ $t(fieldsPrefix + 'monthsSuffix') }}</span>
          </div>
        </div>
        <div class="criteria-col-toggle text-right">
          <b-form-checkbox switch v-model="criterion.value.enabled"></b-form-checkbox>
        </div>
      </div>
    </div>

    <b-alert show variant="light" class="market-fallback-note d-flex align-items-start mt-3 mb-0">
      <i class="fas fa-info-circle text-primary mr-2 mt-1"></i>
      <span class="">{{ $t('efficiencyTestDetails.comparisonCriteria.marketFallbackNote') }}</span>
    </b-alert>

    <div class="d-flex align-items-center justify-content-end mt-3" v-if="noCriteriaActive">
      <span class="font-weight-bold text-danger">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ $t('efficiencyTestDetails.comparisonCriteria.noCriteriaWarning') }}
      </span>
    </div>
  </div>
</template>

<script>
// Criteria list + default `value` shape sourced verbatim from the product spec (critDefs). Field
// names are camelCase here and converted to the backend's snake_case wire format on every request
// (utils/efficiencyTestCriteria) — a new field must still match a backend one once converted
// (`toleranceMonths` → `tolerance_months`), or the backend refuses the criterion.
const CRITERIA_DEFAULTS = [
  { key: 'AcepiCategory', value: { enabled: true } },
  { key: 'MaturityDate', value: { enabled: true, toleranceMonths: 3 } },
  { key: 'IssueDate', value: { enabled: true, toleranceMonths: 3 } },
  { key: 'HasCoupon', value: { enabled: true } },
  { key: 'Currency', value: { enabled: true } },
  { key: 'UnderlyingStructure', value: { enabled: true } },
  { key: 'EarlyRedemption', value: { enabled: true } },
]

export default {
  data() {
    return {
      fieldsPrefix: 'efficiencyTestDetails.comparisonCriteria.fields.',
      criteria: CRITERIA_DEFAULTS.map((c) => ({ key: c.key, value: { ...c.value } })),
    }
  },

  computed: {
    noCriteriaActive() {
      return this.criteria.every((c) => !c.value.enabled)
    },
  },

  methods: {
    // Extra fields are shown/hidden by presence of the matching key in `value`, not by criterion
    // key — any criterion shaped like { toleranceMonths } gets the same UI, e.g. MaturityDate and
    // IssueDate both use the months-tolerance stepper.
    hasField(criterion, field) {
      return Object.prototype.hasOwnProperty.call(criterion.value, field)
    },

    // Read by the parent (EfficiencyTestsCreate.vue) via $refs when the Copertura tab is activated
    // — see TabProfileCoverage.vue / efficiencyTestsCoverageDetail's checkCoverage action.
    getCriteriaPayload() {
      return this.criteria.map((c) => ({ key: c.key, value: { ...c.value } }))
    },

    // Symmetric to getCriteriaPayload() — called via $refs from EfficiencyTestsEdit.vue after
    // efficiencyTestsEditor/loadScoringProfile resolves, to restore a saved test's criteria. `value`
    // request-shaped save() only sends ENABLED criteria (see utils/efficiencyTestCriteria), so a criterion
    // key absent from `criteria` means it was off when saved — default field shapes (e.g.
    // toleranceMonths) are kept from this.criteria, only overlaid with the loaded values.
    setCriteria(criteria) {
      const loadedByKey = {}
      ;(criteria || []).forEach((c) => {
        loadedByKey[c.key] = c.value
      })
      this.criteria = this.criteria.map((c) => {
        const loadedValue = loadedByKey[c.key]
        return loadedValue
          ? { key: c.key, value: { ...c.value, ...loadedValue, enabled: true } }
          : { key: c.key, value: { ...c.value, enabled: false } }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.criteria-table {
  border: 1px solid #ececec;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.criteria-header {
  background: #f6f6f6;
  border-bottom: 1px solid #ececec;
  padding: 11px 20px;
  font-size: 13px;
  font-weight: 700;
  color: #212529;
}

.criteria-row {
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even) {
    background: #fafafa;
  }
}

.criteria-col-label {
  flex: 0 0 270px;
  padding-right: 16px;
}

.criteria-col-toggle {
  flex: 0 0 90px;
}

.market-fallback-note {
  border: 1px solid #dee2e6;
  border-left: 3px solid var(--secondaryColor, #ea633e);
}
</style>
